import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, RefreshCw, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardCls = "rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]";
const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)]/30";

const iso = (d) => d.toISOString().slice(0, 10);
const pct = (n) => `${(Number(n || 0) * 100).toFixed(2)}%`;
const pos = (n) => Number(n || 0).toFixed(1);

const Rows = ({ data, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
          <th className="pb-2 pe-4">Query / Page</th>
          <th className="pb-2 pe-4">Clicks</th>
          <th className="pb-2 pe-4">Impressions</th>
          <th className="pb-2 pe-4">CTR</th>
          <th className="pb-2">Avg. position</th>
        </tr>
      </thead>
      <tbody>
        {data.length === 0 && (
          <tr><td colSpan={5} className="py-6 text-center text-slate-400">No data for this range yet — Google needs a few days after indexing.</td></tr>
        )}
        {data.map((r, i) => (
          <tr key={i} className="border-t border-slate-50">
            <td className="py-2 pe-4 max-w-[280px] truncate font-medium text-[#0b132b]">{r.keys[0]}</td>
            <td className="py-2 pe-4">{r.clicks}</td>
            <td className="py-2 pe-4">{r.impressions}</td>
            <td className="py-2 pe-4">{pct(r.ctr)}</td>
            <td className="py-2">{pos(r.position)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const GscTab = () => {
  const now = new Date();
  const prior = new Date(now);
  prior.setDate(now.getDate() - 28);

  const [status, setStatus] = useState(null);
  const [start, setStart] = useState(iso(prior));
  const [end, setEnd] = useState(iso(now));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/gsc/status`, { withCredentials: true });
      setStatus(data);
      return data;
    } catch {
      setStatus({ configured: false, connected: false });
      return null;
    }
  }, []);

  const loadPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/gsc/performance`, { params: { start, end }, withCredentials: true });
      setData(data);
    } catch (err) {
      if (err.response?.status !== 424) toast.error(err.response?.data?.detail || "Could not load Search Console data.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    (async () => {
      const s = await loadStatus();
      if (s?.connected) loadPerformance();
      const params = new URLSearchParams(window.location.search);
      if (params.get("gsc") === "connected") toast.success("Google Search Console connected.");
      if (params.get("gsc") === "error") {
        const reason = params.get("reason");
        const messages = {
          exchange: "Google token exchange failed — start the connection again.",
          no_refresh_token: "Google did not return a refresh token — reconnect and accept the consent screen.",
          no_property: "No Search Console property for voyage-swiss.com on this Google account — verify the site in Search Console first, then reconnect.",
          api_not_enabled: "The Search Console API is not enabled in your Google Cloud project — enable it (APIs & Services → Library → Google Search Console API), then reconnect.",
          api_error: "Google Search Console API request failed — try again.",
        };
        toast.error(messages[reason] || "Google connection was cancelled or failed.");
      }
    })();
  }, [loadStatus, loadPerformance]);

  const connect = async () => {
    try {
      const { data } = await axios.get(`${API}/gsc/oauth/start`, { withCredentials: true });
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start the Google connection.");
    }
  };

  const disconnect = async () => {
    try {
      await axios.post(`${API}/gsc/disconnect`, {}, { withCredentials: true });
      setStatus((s) => ({ ...s, connected: false, property: null }));
      setData(null);
      toast.success("Disconnected.");
    } catch {
      toast.error("Could not disconnect.");
    }
  };

  const summary = data?.summary?.[0] || {};
  const cards = [
    ["Clicks", summary.clicks ?? "—", "gsc-card-clicks"],
    ["Impressions", summary.impressions ?? "—", "gsc-card-impressions"],
    ["CTR", data ? pct(summary.ctr) : "—", "gsc-card-ctr"],
    ["Avg. position", data ? pos(summary.position) : "—", "gsc-card-position"],
  ];

  return (
    <div className="space-y-6" data-testid="gsc-tab">
      <div className={cardCls}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-lg text-[#0b132b]">Google Search Console</h2>
            <p className="text-xs text-slate-400 mt-1">
              {status?.connected
                ? <>Connected to <span className="font-semibold text-slate-600">{status.property}</span></>
                : "Connect your Google account to see rankings, clicks and keywords here."}
            </p>
          </div>
          {status?.connected ? (
            <button
              onClick={disconnect}
              data-testid="gsc-disconnect-button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 text-slate-500 text-sm font-bold px-5 py-2.5 hover:bg-slate-50 transition-colors"
            >
              <Unlink className="w-4 h-4" /> Disconnect
            </button>
          ) : (
            <button
              onClick={connect}
              disabled={status && !status.configured}
              data-testid="gsc-connect-button"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-50"
            >
              <Link2 className="w-4 h-4" /> Connect Google Search Console
            </button>
          )}
        </div>
        {status && !status.configured && (
          <p className="mt-3 text-xs text-amber-600 bg-amber-50 rounded-xl px-4 py-2.5" data-testid="gsc-not-configured">
            Google credentials are not configured on the server yet.
          </p>
        )}
      </div>

      {status?.connected && (
        <>
          <div className={cardCls}>
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-400">From</span>
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={`${inputCls} mt-1.5`} data-testid="gsc-date-start" />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-400">To</span>
                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={`${inputCls} mt-1.5`} data-testid="gsc-date-end" />
              </label>
              <button
                onClick={loadPerformance}
                disabled={loading}
                data-testid="gsc-apply-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#0b132b] text-white text-sm font-bold px-5 py-2.5 hover:bg-slate-800 transition-colors disabled:opacity-60"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Apply
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map(([label, value, testid]) => (
              <div key={testid} className={cardCls} data-testid={testid}>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p>
                <p className="mt-2 font-display font-bold text-3xl text-[#0b132b]">{value}</p>
              </div>
            ))}
          </div>

          {data?.daily?.length > 0 && (
            <div className={cardCls} data-testid="gsc-daily-chart">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-4">Daily clicks</p>
              <div className="flex items-end gap-1 h-28">
                {data.daily.slice(-60).map((d) => {
                  const max = Math.max(...data.daily.map((x) => x.clicks), 1);
                  return (
                    <div
                      key={d.keys[0]}
                      title={`${d.keys[0]}: ${d.clicks} clicks`}
                      className="flex-1 bg-[var(--brand)]/80 rounded-t-sm hover:bg-[var(--brand)] transition-colors"
                      style={{ height: `${Math.max((d.clicks / max) * 100, 2)}%` }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {data && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className={cardCls}>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Top search queries</p>
                <Rows data={data.queries} testid="gsc-queries-table" />
              </div>
              <div className={cardCls}>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Top pages</p>
                <Rows data={data.pages} testid="gsc-pages-table" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
